import { useAuth } from '../contexts/AuthContext';

export type PlanType = 'free' | 'pro' | 'enterprise';

interface PlanFeatures {
  // Limits
  reportsPerMonth: number;
  maxUsers: number;
  
  // Feature flags
  canExportPDF: boolean;
  canUseWebhooks: boolean;
  canViewStaffMetrics: boolean;
  canViewRevenueLeak: boolean;
  canViewFinancials: boolean;
  canCustomizeAlerts: boolean;
  canAccessAPI: boolean;
  hasMultiLocation: boolean;
  hasTeamAccounts: boolean;
  hasDedicatedSupport: boolean;
}

const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  free: {
    reportsPerMonth: 3,
    maxUsers: 1,
    canExportPDF: false,        // Pro feature
    canUseWebhooks: false,      // Pro feature
    canViewStaffMetrics: true,  // FREE - basic staff view
    canViewRevenueLeak: false,  // Pro feature
    canViewFinancials: true,    // FREE - basic financial view
    canCustomizeAlerts: false,  // Pro feature
    canAccessAPI: false,        // Enterprise feature
    hasMultiLocation: false,    // Enterprise feature
    hasTeamAccounts: false,     // Enterprise feature
    hasDedicatedSupport: false,
  },
  pro: {
    reportsPerMonth: Infinity,
    maxUsers: 1,
    canExportPDF: true,
    canUseWebhooks: true,
    canViewStaffMetrics: true,
    canViewRevenueLeak: true,
    canViewFinancials: true,
    canCustomizeAlerts: true,
    canAccessAPI: false,
    hasMultiLocation: false,
    hasTeamAccounts: false,
    hasDedicatedSupport: false,
  },
  enterprise: {
    reportsPerMonth: Infinity,
    maxUsers: 10,
    canExportPDF: true,
    canUseWebhooks: true,
    canViewStaffMetrics: true,
    canViewRevenueLeak: true,
    canViewFinancials: true,
    canCustomizeAlerts: true,
    canAccessAPI: true,
    hasMultiLocation: true,
    hasTeamAccounts: true,
    hasDedicatedSupport: true,
  },
};

export function usePlanFeatures() {
  const { subscription } = useAuth();
  
  // Also check localStorage directly for admin bypass
  let plan: PlanType = (subscription?.plan as PlanType) || 'free';
  
  try {
    const savedSub = localStorage.getItem('ltss_subscription');
    if (savedSub) {
      const parsed = JSON.parse(savedSub);
      if (parsed.plan === 'enterprise' || parsed.plan === 'pro') {
        plan = parsed.plan;
      }
    }
  } catch (e) {}
  
  const features = PLAN_FEATURES[plan];

  const checkFeature = (feature: keyof PlanFeatures): boolean => {
    const value = features[feature];
    return typeof value === 'boolean' ? value : true;
  };

  const isFeatureLocked = (feature: keyof PlanFeatures): boolean => {
    return !checkFeature(feature);
  };

  const getRequiredPlan = (feature: keyof PlanFeatures): PlanType | null => {
    if (PLAN_FEATURES.free[feature]) return null;
    if (PLAN_FEATURES.pro[feature]) return 'pro';
    if (PLAN_FEATURES.enterprise[feature]) return 'enterprise';
    return null;
  };

  return {
    plan,
    features,
    checkFeature,
    isFeatureLocked,
    getRequiredPlan,
    isPro: plan === 'pro' || plan === 'enterprise',
    isEnterprise: plan === 'enterprise',
    isFree: plan === 'free',
  };
}
